/**
 * Main customer dashboard content
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Paper, Typography
} from '@mui/material';

import CreditCard from '@mui/icons-material/CreditCard';
import LoadingSpinner from '../ui/LoadingSpinner'
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';
import FundingSourceTile from '../paymentsources/FundingSourceTile';
import BalanceAndFloatWidgets from './BalanceAndFloatWidgets';
import SpendingWidgets from './SpendingWidgets';

const abEnvironment = new AbEnvironment();
const CustomerDashboardQuery = graphql`
query CustomerDashboardQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountName
    accountType
    description
    contactLastName
    contactFirstName
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    maxCharge
    floatLimit
    accountStatus
    accountType
    useBudgets
    payFromBalance
    autoTopupActive
    autoTopupAmount
    autoTopupThreshhold
    maxMonthlyBudget
    personaInquiryId
    personaStatus
    verificationComplete
    paymentSourceTsByAccountId(condition: {psStatus: 0} orderBy: PS_ORDER_ASC) {
      totalCount
      nodes {
        psId
        psType
        psStatus
        ccBrand
        ccLast4
        ccExpy
        fiName
        achAccountNum
        firstName
        lastName
        paypalEmail
        declineCount
        lastDeclineReason
        lastDeclineChargeAmount
      }
    }
    activeCards: bindCardTsByProvisioningAccountId (condition:{bcStatus: 0}) {
      totalCount
    }
    pendingCards: bindCardTsByProvisioningAccountId (condition:{bcStatus: -1}) {
      totalCount
    }
    appTsByAccountId {
      totalCount
      nodes {
        appId
        appName
        appSku
        appLogo
        customerAppTsByAppId(condition:{appStatus: 0}) {
          totalCount
          nodes {
            accountTByCustomerAccountId {
              accountId
              accountName
            }
          }
        }
      }
    }
    accountTsByParentId {
      totalCount
      nodes {
        accountId
        accountName
        contactFirstName
        contactLastName
        paymentSourceTsByAccountId(condition:{psStatus: 0}) {
          totalCount
        }
        activeCards: bindCardTsByUsingAccountId(condition:{bcStatus: 0}) {
          totalCount
        }
        pendingCards: bindCardTsByUsingAccountId(condition:{bcStatus: -1}) {
          totalCount
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
        width: '100%',
    },
    rootLoggedIn: {
        flexGrow: 1,
        padding: 20,
    },
    button: {
      paddingLeft: 8,
    },
    buttonout: {
      paddingLeft: 8,
      marginBottom: 12,
    },
    buttonicon: {
      marginRight: 6,
    },
    instructButton: {
      marginTop: -1,
      marginLeft: 3,
      marginRight: 3,
    },
    instructText: {
      marginTop: 8,
      // marginLeft: -11,
    },
    paper: {
        marginTop: 32,
        paddingTop: 16,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        color: theme.palette.text.secondary,
    },
    dbTile: {
      [theme.breakpoints.up('xs')]: {
        padding: theme.spacing(1),
      },
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1),
      },
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2),
      },
      height: 68,
      width: 220
    },
    theTitle: {
        marginTop: 4,
        marginBottom: 15,
    },
    section: {
        marginTop: 12,
        display: 'flex',
        flexWrap: 'wrap',
    },
    gridItem: {
      minWidth: 120,
    },
    sectionHeader: {
        marginTop: 16,
        // marginBottom: 15
    },
    tileWrapper: {
      minWidth: 240,
      marginTop: 16,
      marginLeft: 12,
      marginRight: 12,
      //padding: 12,
    },
    iconp: {
        marginTop: 12
    },
    appnameitem: {
      marginLeft: 8,
    },
    rtable: {
      marginTop: 16,
    },
    moretext: {
      height: 30,
      width: 172,
      paddingTop: 26,
      paddingBottom: 16,
      paddingLeft: 28,
    },
    appgrid: {
      minWidth: 136,
    },
    custlink: {
      marginTop: 4,
      marginBottom: 4,
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 24,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblSumCell: {
      textAlign: 'left'
    },
    tblSumTxt: {
      marginTop: 8,
      marginBottom: 8,
      fontWeight: 'bold'
    }
});

class CustomerDashboard extends Component {

  constructor(props) {
    super(props);

    this.state = {
      currentMaxCharge: 0,
      currentFloatLimit: 0,
      maxCharge: 0,
      floatLimit: 0,
    };  
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  render () {
    const { logError } = this.props.auth;

    const classes = this.props.classes;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={CustomerDashboardQuery}
        variables={{accountId: this.props.accountId}}
        render={({error, props})=> {
          if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
          } else if (props) {
            //temporary code
            /*
            let newActivationOn = false;
            if (localStorage.getItem("verification") === "1") {
              newActivationOn = true;
            }
            */

            let theBudget = props.accountTByAccountId.maxMonthlyBudget;

            let paymentSourceProblems = false;
            let paymentSourceCount = props.accountTByAccountId.paymentSourceTsByAccountId.totalCount;
            let paymentSourceNodes = null;
            if (paymentSourceCount > 0) {
              // We only want the payment sources that can pay for subscriptions.
              let tempNodes = props.accountTByAccountId.paymentSourceTsByAccountId.nodes;
              for (let i=0; i<tempNodes.length; i++) {
                let theNode = tempNodes[i];
                if (theNode.psType === 0 || theNode.psType === 1) {
                  if (paymentSourceNodes == null) {
                    paymentSourceNodes = [];
                  }
                  if (theNode.declineCount > 0) {
                    paymentSourceProblems = true;
                  }
                  paymentSourceNodes.push(theNode);
                }
              }
              if (paymentSourceNodes) {
                paymentSourceCount = paymentSourceNodes.length;
              } else {
                paymentSourceCount = 0;
              }
            }

            let subsActiveCount    = props.accountTByAccountId.activeCards.totalCount;
            let subsPendingCount   = props.accountTByAccountId.pendingCards.totalCount;
            let subsCount          = Number(subsActiveCount) + Number(subsPendingCount);

            /* 
            // for testing
            paymentSourceCount = 0;
            subsCount = 0;
            */

            /** Analyze subscription data **/
            var theApps = [];
            if (props.accountTByAccountId.appTsByAccountId != null &&
                props.accountTByAccountId.appTsByAccountId.nodes != null)
            {
              let appNodes = props.accountTByAccountId.appTsByAccountId.nodes;
              for (let i=0; i<appNodes.length; i++) {
                let sCount = 0;
                let acctList = [];
                if (appNodes[i].customerAppTsByAppId != null) {
                  sCount = appNodes[i].customerAppTsByAppId.totalCount; // Provisioned subscriptions
                  if (sCount === 0) {
                    // No subscriptions, so continue
                    continue;
                  }
                  let acctNodes = appNodes[i].customerAppTsByAppId.nodes;
                  for (let j=0; j<acctNodes.length; j++) {
                    let aname = acctNodes[j].accountTByCustomerAccountId.accountName;
                    let aid   = acctNodes[j].accountTByCustomerAccountId.accountId;
                    if (aname != null) {
                      let attl = aname + '~~~' + aid;
                      // Is it already in the list?
                      let ti = acctList.findIndex((a) => a === attl);
                      if (! ti || ti === -1) {
                        acctList.push(attl);
                      }
                    }
                  }

                  // Sort
                  acctList.sort((a, b) => {
                    if (a > b) { return 1; }
                    if (a < b) { return -1; }
                    return 0;
                  });
                } else {
                  // No subscriptions, continue to next.
                  continue;
                }
                let anApp = {
                  appId:    appNodes[i].appId,
                  appName:  appNodes[i].appName,
                  appSku:   appNodes[i].appSku,
                  appLogo:  appNodes[i].appLogo,
                  subCount: sCount,
                  acctList: acctList
                }

                theApps.push(anApp);
              }

              // Sort in descending count order
              theApps.sort((a, b) => b.subCount - a.subCount);
            }
            // let subsShown = 0;   // Counter for rows in subscription table
            // let remSubsCount = Number(subsCount); // Running total of remaining subs
            
            let startingView = (subsCount === 0);

            let payFromBalance = props.accountTByAccountId.payFromBalance;
            let autoTopupActive = props.accountTByAccountId.autoTopupActive;
            let autoTopupAmount = null;
            let autoTopupThreshold = null;
            if (autoTopupActive === 1) {
              if (Number(props.accountTByAccountId.autoTopupAmount) > 0 && Number(props.accountTByAccountId.autoTopupThreshhold) > 0) {
                autoTopupAmount = Number(props.accountTByAccountId.autoTopupAmount);
                autoTopupThreshold = Number(props.accountTByAccountId.autoTopupThreshhold);
              }
            }

            return (
              <React.Fragment>
                {startingView &&
                <React.Fragment>
                  <Typography variant="h4" align="center" className={classes.theTitle}>
                    Manage subscriptions with AppBind
                  </Typography>
                  <Typography variant="body1">
                    Create <strong>shared emails</strong> to sign up for subscriptions and <strong>virtual credit cards</strong> to pay for subscriptions. 
                  </Typography>
                </React.Fragment>
                }

                {!startingView &&
                <PageTitle title="Dashboard" subtitle="Quick view of funding and balances."/>
                }         

                {/* !newActivationOn &&
                  <AccountSuspendMsg 
                    accountType={props.accountTByAccountId.accountType} 
                    accountStatus={props.accountTByAccountId.accountStatus}
                  />
                */}

                {/* newActivationOn &&
                  <AccountActivationSteps 
                    accountType={props.accountTByAccountId.accountType} 
                    accountStatus={props.accountTByAccountId.accountStatus}
                    personaInquiryId={props.accountTByAccountId.personaInquiryId}
                    personaStatus={props.accountTByAccountId.personaStatus}
                    verificationComplete={props.accountTByAccountId.verificationComplete}
                    auth={this.props.auth}
                    history={this.props.history}/>
                */}

                {/*
                {props.accountTByAccountId.accountType === 3 &&
                  <ResellerProblems history={this.props.history} auth={this.props.auth}/>
                */}

                <Paper className={classes.paper}>
                  {paymentSourceCount === 0 &&
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Funding</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        How will I pay for subscriptions?
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" className={classes.instructText}>
                        Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                      onClick={this.goTo.bind(this, 'fundingsources')}>
                                      <CreditCard className={classes.buttonicon}/> My Funding
                        </Button> to add a funding source. 
                      </Typography>
                    </Grid>
                  </Grid>}

                  {paymentSourceCount > 0 && paymentSourceProblems === true &&
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Funding</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {paymentSourceCount === 1 &&
                      <React.Fragment>
                        <Typography variant="body2">
                          We recommend that you also add a backup funding source.
                        </Typography>
                        <Typography variant="body2" className={classes.instructText}>
                              Go to <Button color="primary"  size="small" variant="outlined" className={classes.instructButton}
                                    onClick={this.goTo.bind(this, 'fundingsources')}>
                                    <CreditCard className={classes.buttonicon}/> My Funding
                          </Button> to add a backup funding source. 
                        </Typography>
                      </React.Fragment>
                      }
                      {paymentSourceCount > 1 &&
                      <Typography variant="body2">
                        There are {paymentSourceCount} funding sources for subscriptions.
                      </Typography>
                      }
                    </Grid>

                    {paymentSourceNodes.map(n => {
                      return (
                      <Grid item className={classes.tileWrapper}>
                        <FundingSourceTile 
                          psType={n.psType}
                          ccBrand={n.ccBrand}
                          ccLast4={n.ccLast4}
                          ccExpy={n.ccExpy}
                          fiName={n.fiName}
                          achAccountNum={n.achAccountNum}
                          firstName={n.firstName}
                          lastName={n.lastName}
                          paypalEmail={n.paypalEmail}
                          declineCount={n.declineCount}
                          lastDeclineReason={n.lastDeclineReason}
                          lastDeclineChargeAmount={n.lastDeclineChargeAmount}
                          drawmode="dashboard"
                        />                        
                      </Grid>
                      )
                    })}
                  </Grid>}

                  {paymentSourceCount > 0 && paymentSourceProblems === false &&
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Funding</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {paymentSourceCount === 1 &&
                      <React.Fragment>
                        <Typography variant="body2">
                          We recommend that you also add a backup funding source.
                        </Typography>
                        <Typography variant="body2" className={classes.instructText}>
                              Go to <Button color="primary"  size="small" variant="outlined" className={classes.instructButton}
                                    onClick={this.goTo.bind(this, 'fundingsources')}>
                                    <CreditCard className={classes.buttonicon}/> My Funding
                          </Button> to add a backup funding source. 
                        </Typography>
                      </React.Fragment>}
                      {paymentSourceCount > 1 &&
                      <Typography variant="body2">
                        There are {paymentSourceCount} funding sources for subscriptions.
                      </Typography>}
                    </Grid>
                    <Grid item className={classes.tileWrapper}>
                      <FundingSourceTile 
                        psType={paymentSourceNodes[0].psType}
                        ccBrand={paymentSourceNodes[0].ccBrand}
                        ccLast4={paymentSourceNodes[0].ccLast4}
                        ccExpy={paymentSourceNodes[0].ccExpy}
                        fiName={paymentSourceNodes[0].fiName}
                        achAccountNum={paymentSourceNodes[0].achAccountNum}
                        firstName={paymentSourceNodes[0].firstName}
                        lastName={paymentSourceNodes[0].lastName}
                        paypalEmail={paymentSourceNodes[0].paypalEmail}
                        drawmode="dashboard"
                      />                        
                    </Grid>
                    {paymentSourceCount > 1 &&
                    <Grid item className={classes.tileWrapper}>
                      <FundingSourceTile 
                        psType={paymentSourceNodes[1].psType}
                        ccBrand={paymentSourceNodes[1].ccBrand}
                        ccLast4={paymentSourceNodes[1].ccLast4}
                        ccExpy={paymentSourceNodes[1].ccExpy}
                        fiName={paymentSourceNodes[1].fiName}
                        achAccountNum={paymentSourceNodes[1].achAccountNum}
                        firstName={paymentSourceNodes[1].firstName}
                        lastName={paymentSourceNodes[1].lastName}
                        paypalEmail={paymentSourceNodes[1].paypalEmail}
                        drawmode="dashboard"
                      />                        
                    </Grid>}
                    {paymentSourceCount === 3 &&
                    <Grid item className={classes.tileWrapper}>
                      <FundingSourceTile 
                        psType={paymentSourceNodes[2].psType}
                        ccBrand={paymentSourceNodes[2].ccBrand}
                        ccLast4={paymentSourceNodes[2].ccLast4}
                        ccExpy={paymentSourceNodes[2].ccExpy}
                        fiName={paymentSourceNodes[2].fiName}
                        achAccountNum={paymentSourceNodes[2].achAccountNum}
                        firstName={paymentSourceNodes[2].firstName}
                        lastName={paymentSourceNodes[2].lastName}
                        paypalEmail={paymentSourceNodes[2].paypalEmail}
                        drawmode="dashboard"
                      />                        
                    </Grid>}
                    {paymentSourceCount > 3 &&
                    <Grid item className={classes.tileWrapper}>
                      <Paper>
                        <div className={classes.moretext}>
                          <Typography variant="body2" align="center">
                            ... {paymentSourceCount - 2} more funding sources ...
                          </Typography>
                        </div>
                      </Paper>
                    </Grid>}
                  </Grid>}
                </Paper>

                {! startingView &&
                <Paper className={classes.paper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your AppBind balance</Typography>
                      {props.accountTByAccountId.useBudgets >= 1 &&
                      <Typography variant="body2">
                        Subscriptions and services are paid from your available balance.  AppBind also may provide you with an overdraft float that allows purchases to exceed your available balance.
                      </Typography>}
                      {props.accountTByAccountId.useBudgets === 0 &&
                      <Typography variant="body2">
                        AppBind provides you with a float for to pay for subscriptions and services. You may also use your available balance to pay.
                      </Typography>}
                    </Grid>
                  
                    <Grid item xs={12}>
                      <BalanceAndFloatWidgets
                        auth={this.props.auth}
                        showEditLink={true}
                        payFromBalance={payFromBalance}
                        autoTopupAmount={autoTopupAmount}
                        autoTopupThreshold={autoTopupThreshold}
                        showLimitRequest={false}
                        floatLimit={props.accountTByAccountId.floatLimit}
                        accountId={this.props.accountId}
                        accountType={props.accountTByAccountId.accountType}
                        useBudgets={props.accountTByAccountId.useBudgets}
                        history={this.props.history}
                      />
                    </Grid>
                  </Grid>
                </Paper>}

                {! startingView &&
                <Paper className={classes.paper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>Your spending</Typography>
                      {props.accountTByAccountId.useBudgets >= 1 &&
                      <Typography variant="body2">
                        AppBind tracks your spending and provides budget and spend controls.
                      </Typography>}
                      {props.accountTByAccountId.useBudgets === 0 &&
                      <Typography variant="body2">
                        AppBind tracks your spending and provides spend controls.
                      </Typography>}
                    </Grid>
                  
                    <Grid item xs={12}>
                      <SpendingWidgets 
                        accountId={this.props.accountId} 
                        spendLimit={theBudget}
                        showEditLink={true}
                        history={this.props.history}/>
                    </Grid>
                  </Grid>
                </Paper>}

                {/* 
                <Paper className={classes.paper}>
                  {subsCount === 0 &&
                  <Grid container>
                    <Grid item xs={12}>
                      {paymentSourceCount === 0 &&
                      <Typography variant="h6" className={classes.sectionTitle}>Subscriptions</Typography>}
                      {paymentSourceCount > 0 &&
                      <Typography variant="h6" className={classes.sectionTitle}>Subscriptions</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        What subscriptions have I purchased?
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" className={classes.instructText}>
                          Go to <Button color="primary" size="small" variant="outlined" className={classes.instructButton}
                                        onClick={this.goTo.bind(this, 'subscriptioncreateself')}>
                                      <Add className={classes.buttonicon}/>Buy Subscription
                          </Button> to add a subscription. 
                        </Typography>
                      }
                    </Grid>
                  </Grid>}

                  {subsCount > 0 &&
                  <React.Fragment>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="h6" className={classes.sectionTitle}>Subscriptions</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {subsCount === 1 &&
                        <Typography variant="body2">
                          There is one subscription.
                        </Typography>
                        }
                        {subsCount > 1 &&
                        <Typography variant="body2">
                          There are {subsCount} subscriptions.
                        </Typography>
                        }
                      </Grid>
                    </Grid>

                    {theApps != null && theApps.length > 0 &&
                    <ResponsivexxxTable
                      headers={[
                        {label:'App', css: 'padding-top: 11px' },
                        'Subscriptions',
                        'Subscribers'
                      ]}
                    >
                      <Table className={classes.rtable}>
                        <TableHead className={classes.tblHead}>
                          <TableRow className={classes.tblHeadRow}>
                            <TableCell className={classes.tblHeadCell}>App</TableCell>
                            <TableCell className={classes.tblHeadCell}>Subscriptions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {theApps.map(n => {
                            subsShown++;
                            if (subsShown > 5) {
                              // Do nothing
                              return;
                            } else if (subsShown < 5) {
                              remSubsCount = remSubsCount - Number(n.subCount);
                              // Get list of accounts to show
                              let acctList = n.acctList;
                              let theLimit = 5
                              let theLinks = [];
                              let accountCounter = 0;
                              for (let k=0; k<acctList.length; k++) {
                                let acTxt = acctList[k];
                                let acArr = acTxt.split("~~~");
                                let thehref = null;
                                if (acArr.length > 1) {
                                  thehref = "customer?accountId=" + acArr[1] + "&fp=Dashboard";
                                }

                                if (k >= theLimit) {
                                  let theDiff = acctList.length - theLimit;
                                  if (theDiff > 1) {
                                    theLinks.push(", ");
                                    if (thehref != null) {
                                      theLinks.push(<Link key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                    } else {
                                      theLinks.push({acTxt});
                                    }
                                    theLinks.push(', and ' + theDiff + ' more');
                                  } else {
                                    theLinks.push(", and ");
                                    if (thehref != null) {
                                        theLinks.push(<Link key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                    } else {
                                      theLinks.push({acTxt});
                                    }
                                  }
                                  break;
                                }

                                if (theLinks.length === 0) {
                                  if (thehref != null) {
                                    theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                  } else {
                                    theLinks.push({acTxt});
                                  }
                                } else if (k === acctList.length - 1) {
                                  theLinks.push(", and ");
                                  if (thehref != null) {
                                    theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                  } else {
                                    theLinks.push({acTxt});
                                  }
                                } else {
                                  theLinks.push(", ");
                                  if (thehref != null) {
                                    theLinks.push(<Link  key={'sub' + subsShown + '-' + accountCounter++} href={thehref}>{acArr[0]}</Link>);
                                  } else {
                                    theLinks.push({acTxt});
                                  }
                                }
                              }
                              let aUrl = "app?appId=" + n.appId + "&fp=Dashboard";

                              return (
                                <TableRow key={n.appId}>
                                  <TableCell>
                                    <Grid container alignItems="center" className={classes.appgrid}>
                                      <Grid item>
                                        <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px' style={{marginTop: '4px'}}/>
                                      </Grid>
                                      <Grid item className={classes.appnameitem}>
                                        <Link component="button" 
                                              variant="body2"
                                              onClick={this.goTo.bind(this, aUrl)}>
                                          {n.appName}
                                        </Link>
                                        {n.appSku != null && n.appSku !== '' && n.appSku !== n.appName &&
                                        <Typography variant="body2">{n.appSku}</Typography>
                                        }
                                      </Grid>
                                    </Grid>
                                  </TableCell>
                                  <TableCell>
                                    {n.subCount !== 1 &&
                                    <Typography variant="body2">{n.subCount} subscriptions ({theLinks})</Typography>}
                                    {n.subCount === 1 &&
                                    <Typography variant="body2">{n.subCount} subscription ({theLinks})</Typography>}
                                  </TableCell>
                                </TableRow>
                              );
                            } else {
                              let remAppsCount = theApps.length - 5;
                              return (
                                <TableRow key={'moresubs'}>
                                  <TableCell className={classes.tblSumCell}>
                                    <Typography variant="body2" className={classes.tblSumTxt}>{remAppsCount} more apps</Typography>
                                  </TableCell>
                                  <TableCell className={classes.tblSumCell}>
                                    <Typography variant="body2" className={classes.tblSumTxt}>{remSubsCount} more subscriptions</Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                          })}
                        </TableBody>
                      </Table>
                    </ResponsivexxxTable>}
                  </React.Fragment>}
                </Paper>
                */}
              </React.Fragment>
            );
          }
          return <LoadingSpinner />
        }}
      />
    );
  }
}

export default withStyles(styleSheet)(CustomerDashboard);
